import React, {useState} from 'react'
import {useAppState} from '../../state'
import {useTranslation} from 'react-i18next'
import {InputField} from '../tasks/CompletedTasks'
import {alarmTypeToTranslation} from './formatters'
import {Button, DatePicker, Input, Modal, Select, SelectOption} from '../../sharedComponents/components'
import {ColoredTogglerButton} from '../../Components/Atoms/ColoredTogglerButton'
import styled from 'styled-components'
import {colors} from '../../sharedComponents/colors'
import IsUserHealthInspector from '../../hooks/userIsHealthInspector'
import InputWithTags from '../tasks/GeneralTasks/InputWithTags'

export interface AlarmsFilters {
  alarmTypes: SelectOption[]
  completedBy: SelectOption[]
  startDate: Date
  endDate: Date
  media?: boolean
}

export interface AlarmsFilterViewProps {
  locale: string
  selectedFilter: AlarmsFilters
  setFilters: (key: string, value: any) => void
  resetFilters: () => void
  applyFilters: (overrides?: Partial<AlarmsFilters>) => void
  onResolveAllTaskAlarms: (formData: {resolution: string; resolvedBy: string}) => Promise<any>
  onResolveAllApplianceAlarms: (formData: {resolution: string; resolvedBy: string}) => Promise<any>
}

type BulkResolveModalType = 'task' | 'appliance' | null

export const AlarmsFilterView = ({
  resetFilters,
  applyFilters,
  selectedFilter,
  setFilters,
  locale,
  onResolveAllTaskAlarms,
  onResolveAllApplianceAlarms
}: AlarmsFilterViewProps) => {
  const {state} = useAppState()
  const {t} = useTranslation(['alarms', 'tasks'])
  const {filters} = state.v1.alarms
  const {isHealthInspector} = IsUserHealthInspector()
  const siteId = state.site?.id
  const disableBulkResolve =
    isHealthInspector ||
    (!state.me?.accessRights?.superuser && !(siteId && state.me?.accessRights?.write?.sites?.includes(siteId)))
  const canBulkResolve = !disableBulkResolve
  const actorSuggestions = state.site?.actorSuggestions ?? []
  const hasActorSuggestions = actorSuggestions.length > 0
  const [bulkResolveType, setBulkResolveType] = useState<BulkResolveModalType>(null)
  const [formData, setFormData] = useState({resolution: '', resolvedBy: ''})
  const [submitting, setSubmitting] = useState(false)

  const resetBulkResolveModal = () => {
    setBulkResolveType(null)
    setFormData({resolution: '', resolvedBy: ''})
    setSubmitting(false)
  }

  const handleResolvedByChange = (resolvedBy: string) => {
    setFormData(prev => ({...prev, resolvedBy}))
  }

  const onBulkResolveSubmit = async (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault()
    if (
      (formData.resolution ?? '').trim().length === 0 ||
      (formData.resolvedBy ?? '').trim().length === 0 ||
      submitting ||
      bulkResolveType === null
    ) {
      return
    }

    setSubmitting(true)
    try {
      if (bulkResolveType === 'task') {
        await onResolveAllTaskAlarms(formData)
      } else {
        await onResolveAllApplianceAlarms(formData)
      }
      resetBulkResolveModal()
    } finally {
      setSubmitting(false)
    }
  }

  const bulkResolveTitle =
    bulkResolveType === 'task'
      ? t('alarms:buttons.resolveAllTaskAlarms', 'Resolve all task alarms')
      : t('alarms:buttons.resolveAllApplianceAlarms', 'Resolve all appliance alarms')

  const activeTaskAlarmsCount = state.v1.alarms.activeTaskAlarmsCount
  const activeApplianceAlarmsCount = state.v1.alarms.activeApplianceAlarmsCount

  return (
    <>
      <InputField>
        <label htmlFor="triggeredTime">{t('alarms:labels.filter.triggeredTimeHeading', 'Triggered time')}</label>
        <DatePicker
          id="triggeredTime"
          onChange={setFilters}
          startDate={selectedFilter.startDate}
          endDate={selectedFilter.endDate}
          locale={locale}
        />
      </InputField>
      <InputField>
        <label htmlFor="alarmTypes">{t('alarms:labels.filter.alarmTypeHeading', 'Alarm type')}</label>
        <Select
          id="alarmTypes"
          onChange={value => setFilters('alarmTypes', value)}
          value={selectedFilter.alarmTypes}
          placeholder={t('tasks:labels.selectFilters', 'Select...')}
          options={filters.alarmTypes.map(v => ({value: v, label: alarmTypeToTranslation(t, v)}))}
          multiple
        />
      </InputField>
      {/* <InputField>
        <label htmlFor="completedBy">{t('tasks:labels.columnNameCompletedBy', 'Completed By')}</label>
        <Select
          id="completedBy"
          onChange={(value: any) => setFilters('completedBy', value)}
          value={selectedFilter.completedBy}
          placeholder={t('tasks:labels.selectFilters', 'Select...')}
          options={filters.completedBy.map(value => ({value: value.id, label: value.name}))}
          multiple
        />
      </InputField> */}
      <InputField>
        <label style={{display: 'flex', alignItems: 'center', gap: '0.5rem'}}>{t('tasks:labels.media', 'Image')}</label>
        <ColoredTogglerButton
          checked={!!selectedFilter.media}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            const newState = !!e.target.checked
            setFilters('media', newState)
          }}
        />
      </InputField>
      <div style={{display: 'flex', justifyContent: 'space-between'}}>
        <Button buttonStyle="secondary" buttonSize="small" onClick={resetFilters}>
          {t('tasks:buttons.resetFilters', 'Reset')}
        </Button>
        <Button buttonStyle="primary" buttonSize="small" onClick={() => applyFilters()}>
          {t('tasks:buttons.applyFilters', 'Apply filters')}
        </Button>
      </div>
      {canBulkResolve && (
        <BulkResolveButtonsContainer>
          <Button
            buttonStyle="secondary"
            buttonSize="small"
            onClick={() => setBulkResolveType('task')}
            disabled={activeTaskAlarmsCount === 0}
          >
            {t('alarms:buttons.resolveAllTaskAlarms', 'Resolve all task alarms')}
          </Button>
          <Button
            buttonStyle="secondary"
            buttonSize="small"
            onClick={() => setBulkResolveType('appliance')}
            disabled={activeApplianceAlarmsCount === 0}
          >
            {t('alarms:buttons.resolveAllApplianceAlarms', 'Resolve all appliance alarms')}
          </Button>
        </BulkResolveButtonsContainer>
      )}
      <Modal
        isOpen={bulkResolveType !== null}
        onClose={submitting ? undefined : resetBulkResolveModal}
        title={bulkResolveTitle}
        minHeight="320px"
        maxWidth="578px"
        headerHeight="64px"
        headerBackgroundColor={colors.system.red}
        headerLeftItem={
          <Button buttonStyle="secondary" buttonSize="small" onClick={resetBulkResolveModal} disabled={submitting}>
            {t('common:buttons.cancel', 'Cancel')}
          </Button>
        }
        headerRightItem={
          <Button
            buttonStyle="primary"
            buttonSize="small"
            onClick={onBulkResolveSubmit}
            disabled={
              (formData.resolution ?? '').trim().length === 0 ||
              (formData.resolvedBy ?? '').trim().length === 0 ||
              submitting ||
              disableBulkResolve
            }
          >
            {t('common:buttons.resolve', 'Resolve')}
          </Button>
        }
      >
        <BulkResolveModalContent>
          {hasActorSuggestions ? (
            <InputWithTags
              label={t('appliances:labels.resolvedBy', 'Resolved By')}
              placeholder={t('appliances:placeholders.resolvedBy', 'Enter name')}
              savedSuggestions={formData.resolvedBy}
              defaultSuggestions={actorSuggestions}
              onChange={(_key, selected) => handleResolvedByChange(selected[0] ?? '')}
              disabled={submitting || disableBulkResolve}
              required
            />
          ) : (
            <Input
              id="bulkResolveAlarmModalFormResolved"
              name="bulkResolveAlarmModalFormResolved"
              label={t('appliances:labels.resolvedBy', 'Resolved By')}
              placeholder={t('appliances:placeholders.resolvedBy', 'Enter name')}
              onChange={(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                handleResolvedByChange(event.target.value)
              }
              value={formData.resolvedBy}
              disabled={submitting || disableBulkResolve}
              required
            />
          )}
          <Input
            id="bulkResolveAlarmModalFormReason"
            name="bulkResolveAlarmModalFormReason"
            label={t('appliances:labels.description', 'Description')}
            placeholder={t('appliances:placeholders.resolution', 'Enter alarm reason & resolution')}
            multiline
            rows={6}
            onChange={(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
              setFormData(prev => ({...prev, resolution: event.target.value}))
            }
            value={formData.resolution}
            disabled={submitting || disableBulkResolve}
            required
          />
        </BulkResolveModalContent>
      </Modal>
    </>
  )
}

const BulkResolveButtonsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-top: 3rem;
`

const BulkResolveModalContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 1.5rem;
`
